import { useFormContext } from 'react-hook-form';
import type { CreativeText } from '../types';
import { usePagination } from 'hooks/usePagination';
import { useCreativeTexts } from '../api';
import { useCreativeTextsColumns, useGridSelection } from '../hooks';
import { BaseGrid } from 'components/common';
import { SuspenseBoundary } from 'components/common';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { debounce } from 'lodash-es';
import { useListParams } from 'hooks';
import { usePermissionGuard } from '../../../login';

const CreativeTextsGridComponent = memo(() => {
  const { accessToChangeOriginaltextcreative } = usePermissionGuard();
  const navigate = useNavigate();
  const { watch } = useFormContext();
  const pagination = usePagination([], 50);
  const { setApps, apps: urlApps } = useListParams();
  console.log(urlApps);
  const [debouncedFilters, setDebouncedFilters] = useState<{
    apps: string | undefined;
  }>({
    apps: Array.isArray(urlApps) ? (urlApps[0] ?? undefined) : undefined,
  });

  const filterByApps = watch('apps') as string[];

  const debouncedUpdate = useCallback(
    (apps?: string[]) => {
      setDebouncedFilters({ apps: apps?.[0] ?? undefined });
      setApps(apps ?? undefined);
    },
    [setDebouncedFilters, setApps],
  );

  const debouncedFn = useMemo(
    () => debounce(debouncedUpdate, 500),
    [debouncedUpdate],
  );

  useEffect(() => {
    debouncedFn(filterByApps);
    return () => {
      debouncedFn.cancel();
    };
  }, [filterByApps, debouncedFn]);

  const { selectedRows, handleSelectionChange } =
    useGridSelection('selectedAdsHistory');

  const columns = useCreativeTextsColumns();

  const {
    data: { data: creativeTexts, count },
    isLoading,
  } = useCreativeTexts({
    params: {
      apps: debouncedFilters.apps ?? undefined,
      desc: true,
      order_by: 'created',
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const handleClick = useCallback(
    (id: number) => {
      accessToChangeOriginaltextcreative && navigate(`/cabinet/facebook/creative-texts/${id}`);
    },
    [navigate, accessToChangeOriginaltextcreative],
  );

  return (
    <BaseGrid<CreativeText>
      rows={creativeTexts}
      columns={columns}
      count={count}
      isLoading={isLoading}
      pagination={pagination}
      rowSelectionModel={selectedRows}
      onRowSelectionModelChange={handleSelectionChange}
      getRowId={(row) => row.id}
      onRowClick={(params) => {
        handleClick((params.row as CreativeText).id);
      }}
    />
  );
});

CreativeTextsGridComponent.displayName = 'CreativeTextsGridComponent';

const CreativeTextsGrid = () => {
  return (
    <SuspenseBoundary>
      <CreativeTextsGridComponent />
    </SuspenseBoundary>
  );
};

export default CreativeTextsGrid;
